<template>
  <div class="bg-[#121419] text-white min-h-screen relative">
    <div class="absolute -top-[101px] z-0 left-0 w-full h-[500px] overflow-hidden">
      <div
        v-for="(img, index) in backgroundImages"
        :key="index"
        class="absolute inset-0 transition-opacity duration-1000 bg-center bg-cover bg-no-repeat"
        :style="{
          backgroundImage: `url(${img})`,
          opacity: currentBgIndex === index ? 0.15 : 0,
          zIndex: currentBgIndex === index ? 1 : 0,
        }"
      ></div>
      <div class="absolute inset-0 bg-gradient-to-b from-transparent to-[#121419] z-10"></div>
    </div>

    <div class="relative z-1 py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <h1 class="text-3xl font-bold !mb-12 text-center">{{ $t('faq_frequency_asked_questions') }}</h1>

        <div class="mb-16">
          <h2 class="text-xl font-semibold mb-8 text-white">{{ $t('faq_common_questions') }}</h2>
          
          <div class="flex flex-wrap md:mx-[-24px] lg:mx-[-32px]">
            <div
              v-for="(item, index) in mainQuestions"
              :key="`main-${index}`"
              class="w-full md:w-1/2 md:px-6 lg:px-8 pb-8"
            >
              <div class="bg-[#1E2330] p-6 rounded-lg border border-gray-700 h-full flex flex-col">
                <h3 class="text-xl font-medium mb-4">{{ item.question }}</h3>
                <div
                  class="text-gray-300 text-base leading-relaxed flex-grow"
                  v-html="item.answer.replace(/\n/g, '<br />')"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-16">
          <h2 class="text-xl font-semibold mb-6 text-white">{{ $t('faq_more_questions') }}</h2>
          <div class="space-y-4">
            <div
              v-for="(item, index) in expandableQuestions"
              :key="`expand-${index}`"
              class="border border-gray-700 rounded-lg overflow-hidden bg-[#1E2330]"
            >
              <button
                class="w-full p-6 text-left flex justify-between items-center focus:outline-none cursor-pointer"
                @click="toggleQuestion(index)"
              >
                <div class="flex items-center">
                  <component :is="item.icon" class="h-5 w-5 text-[#6CBFFF] mr-3 flex-shrink-0" />
                  <h3 class="text-lg font-medium">{{ item.question }}</h3>
                </div>
                <component 
                  :is="expandedQuestions.includes(index) ? ChevronUpIcon : ChevronDownIcon" 
                  class="h-5 w-5 text-[#4FD1C5] ml-3 flex-shrink-0 transition-transform duration-300" 
                />
              </button>
              <div 
                class="overflow-hidden transition-all duration-300 ease-in-out"
                :style="{ 
                  maxHeight: expandedQuestions.includes(index) ? `${getContentHeight(`question-${index}`)}px` : '0px',
                  opacity: expandedQuestions.includes(index) ? 1 : 0
                }"
              >
                <div :ref="`question-${index}`" class="px-6 pb-6 pl-14" v-html="item.answer"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-8 mb-16">
          <div class="border border-gray-700 rounded-lg overflow-hidden bg-[#1E2330] flex flex-col">
            <button class="w-full text-left focus:outline-none p-0 cursor-pointer" @click="toggleTutorial('gettingStarted')">
              <div class="relative flex-shrink-0">
                <img
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/sergiodelrio_Ted_Talk_from_the_speakers_point_of_view_formal_fe_cc27e10a-5e61-49d3-8049-d1a9d40296af%20-%20Copy-1RbpJJOARR792caYGXCw3cAQJP6H6s.png"
                  alt="Getting Started Tutorial"
                  class="w-full h-48 object-cover"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-[#121419] to-transparent opacity-70"></div>
                <div class="absolute bottom-0 left-0 p-6 flex justify-between items-center w-full">
                  <h2 class="text-xl font-semibold text-white">{{ $t('faq_getting_started_tutorial') }}</h2>
                  <component 
                    :is="expandedTutorials.includes('gettingStarted') ? ChevronUpIcon : ChevronDownIcon" 
                    class="h-6 w-6 text-[#6CBFFF] transition-transform duration-300" 
                  />
                </div>
              </div>
            </button>
            <div 
              class="overflow-hidden transition-all duration-500 ease-in-out"
              :style="{ 
                maxHeight: expandedTutorials.includes('gettingStarted') ? `${getContentHeight('tutorial-gs')}px` : '0px',
                opacity: expandedTutorials.includes('gettingStarted') ? 1 : 0
              }"
            >
              <div ref="tutorial-gs" class="p-6">
                <div class="sm:space-y-12 grid grid-rows-6 md:grid-rows-3 gap-6 grid-flow-col">
                  <div
                    v-for="(step, stepIndex) in gettingStartedSteps"
                    :key="`gs-step-${stepIndex}`"
                    class="items-start transition-opacity duration-500"
                    :style="{
                      opacity: expandedTutorials.includes('gettingStarted') ? 1 : 0,
                    }"
                  >
                    <div>
                      <h3 class="text-lg font-medium mb-2 flex items-center">
                        <span class="bg-[#6CBFFF] text-[#111827] w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          {{ stepIndex + 1 }}
                        </span>
                        {{ step.title }}
                      </h3>
                      <p class="text-gray-300">{{ step.description }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="border border-gray-700 rounded-lg overflow-hidden bg-[#1E2330] flex flex-col">
            <button class="w-full text-left focus:outline-none p-0 cursor-pointer" @click="toggleTutorial('schoolInterpretation')">
              <div class="relative flex-shrink-0">
                <img
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/sergiodelrio_woman_giving_a_lecture_holding_a_microphone_--ch_16970537-60e2-47a2-b705-0fe6b37843b8_1-uLZtygN8gSqD0Lu2EtKpPvrsr8Upou.png"
                  alt="Interpretations for School Tutorial"
                  class="w-full h-48 object-cover"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-[#121419] to-transparent opacity-70"></div>
                <div class="absolute bottom-0 left-0 p-6 flex justify-between items-center w-full">
                  <h2 class="text-xl font-semibold text-white">{{ $t('faq_school_interpretation_tutorial') }}</h2>
                  <component 
                    :is="expandedTutorials.includes('schoolInterpretation') ? ChevronUpIcon : ChevronDownIcon" 
                    class="h-6 w-6 text-[#6CBFFF] transition-transform duration-300" 
                  />
                </div>
              </div>
            </button>
            <div 
              class="overflow-hidden transition-all duration-500 ease-in-out"
              :style="{ 
                maxHeight: expandedTutorials.includes('schoolInterpretation') ? `${getContentHeight('tutorial-si')}px` : '0px',
                opacity: expandedTutorials.includes('schoolInterpretation') ? 1 : 0
              }"
            >
              <div ref="tutorial-si" class="p-6">
                <div class="sm:space-y-12 grid grid-rows-6 md:grid-rows-3 gap-6 grid-flow-col">
                  <div
                    v-for="(step, stepIndex) in schoolInterpretationSteps"
                    :key="`si-step-${stepIndex}`"
                    class="items-start transition-opacity duration-500"
                    :style="{
                      opacity: expandedTutorials.includes('schoolInterpretation') ? 1 : 0,
                    }"
                  >
                    <div>
                      <h3 class="text-lg font-medium mb-2 flex items-center">
                        <span class="bg-[#6CBFFF] text-[#111827] w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          {{ stepIndex + 1 }}
                        </span>
                        {{ step.title }}
                      </h3>
                      <p class="text-gray-300">{{ step.description }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  GlobeAltIcon, 
  VideoCameraIcon, 
  LinkIcon, 
  UserPlusIcon, 
  LanguageIcon 
} from '@heroicons/vue/24/outline';

export default {
  components: {
    ChevronDownIcon, 
    ChevronUpIcon, 
    GlobeAltIcon, 
    VideoCameraIcon, 
    LinkIcon, 
    UserPlusIcon, 
    LanguageIcon
  },
  data() {
    return {
      expandedQuestions: [],
      expandedTutorials: [],
      currentBgIndex: 0,
      contentHeights: {},
      interval: null,
      backgroundImages: [
        require("@/assets/media/faq-carousel-1.png"),
        require("@/assets/media/faq-carousel-2.png")
      ],
    };
  },
  
  computed: {
    mainQuestions() {
      return [
        {
          question: this.$t('faq_in_which_scenarios_can_forum_be_used'),
          answer: this.$t('faq_answer_in_which_scenarios_can_forum_be_used')
        },
        {
          question: this.$t('faq_can_i_join_a_session_without_an_account'),
          answer: this.$t('faq_answer_can_i_join_a_session_without_an_account')
        },
        {
          question: this.$t('faq_what_are_the_requirements_and_best_practices_to_use_forum'),
          answer: this.$t('faq_answer_what_are_the_requirements_and_best_practices_to_use_forum')
        },
        {
          question: this.$t('faq_can_i_add_specific_terminology_for_my_event'),
          answer: this.$t('faq_answer_can_i_add_specific_terminology_for_my_event')
        },
      ];
    },
    expandableQuestions() {
      const questions = [
        {
          question: this.$t('faq_which_languages_and_dialects_can_forum_translate'),
          answer: this.$t('faq_answer_which_languages_and_dialects_can_forum_translate'),
          icon: GlobeAltIcon,
        },
        {
          question: this.$t('faq_whats_the_difference_on_each_recording_mode'),
          answer: this.$t('faq_answer_whats_the_difference_on_each_recording_mode'),
          icon: VideoCameraIcon,
        },
        {
          question: this.$t('faq_how_do_i_invite_people_into_my_session'),
          answer: this.$t('faq_answer_how_do_i_invite_people_into_my_session'),
          icon: LinkIcon,
        },
        {
          question: this.$t('faq_whats_the_difference_between_sponsor_host_participant_guest'),
          answer: this.$t('faq_answer_whats_the_difference_between_sponsor_host_participant_guest'),
          icon: UserPlusIcon,
        },
        {
          question: this.$t('faq_whats_the_difference_between_closed_captions_and_chroma_key'),
          answer: this.$t('faq_answer_whats_the_difference_between_closed_captions_and_chroma_key'),
          icon: LanguageIcon,
        }
      ];
      return questions.map((item) => ({
        ...item,
        answer: item.answer.replace(/text-\[#4FD1C5\]/g, 'text-[#6CBFFF]').replace(/bg-\[#4FD1C5\]/g, 'bg-[#6CBFFF]')
      }));
    },
    gettingStartedSteps() {
      return [
        {
          title: this.$t('faq_getting_started_step1_title'),
          description: this.$t('faq_getting_started_step1_description'),
        },
        {
          title: this.$t('faq_getting_started_step2_title'),
          description: this.$t('faq_getting_started_step2_description'),
        },
        {
          title: this.$t('faq_getting_started_step3_title'),
          description: this.$t('faq_getting_started_step3_description'),
        },
        {
          title: this.$t('faq_getting_started_step4_title'),
          description: this.$t('faq_getting_started_step4_description'),
        },
        {
          title: this.$t('faq_getting_started_step5_title'),
          description: this.$t('faq_getting_started_step5_description'),
        },
        {
          title: this.$t('faq_getting_started_step6_title'),
          description: this.$t('faq_getting_started_step6_description'),
        }
      ];
    },
    
    schoolInterpretationSteps() {
      return [
        {
          title: this.$t('faq_school_interpretation_step1_title'),
          description: this.$t('faq_school_interpretation_step1_description'),
        },
        {
          title: this.$t('faq_school_interpretation_step2_title'),
          description: this.$t('faq_school_interpretation_step2_description'),
        },
        {
          title: this.$t('faq_school_interpretation_step3_title'),
          description: this.$t('faq_school_interpretation_step3_description'),
        },
        {
          title: this.$t('faq_school_interpretation_step4_title'),
          description: this.$t('faq_school_interpretation_step4_description'),
        },
        {
          title: this.$t('faq_school_interpretation_step5_title'),
          description: this.$t('faq_school_interpretation_step5_description'),
        },
        {
          title: this.$t('faq_school_interpretation_step6_title'),
          description: this.$t('faq_school_interpretation_step6_description'),
        }
      ];
    }
  },
  
  methods: {
    // Get content height for smooth transitions
    getContentHeight(refName) {
      if (!this.contentHeights[refName]) {
        return 1000; // Default height to ensure content is visible
      }
      return this.contentHeights[refName];
    },
    
    // Update content heights after DOM updates
    async updateContentHeights() {
      await this.$nextTick();
      
      // Update question heights
      this.expandableQuestions.forEach((_, index) => {
        const refName = `question-${index}`;
        const el = document.querySelector(`[ref="${refName}"]`);
        if (el) {
          this.contentHeights[refName] = el.scrollHeight;
        }
      });
      
      // Update tutorial heights
      ['tutorial-gs', 'tutorial-si'].forEach(refName => {
        const el = document.querySelector(`[ref="${refName}"]`);
        if (el) {
          this.contentHeights[refName] = el.scrollHeight;
        }
      });
    },
    
    // Toggle functions
    async toggleQuestion(index) {
      if (this.expandedQuestions.includes(index)) {
        this.expandedQuestions = this.expandedQuestions.filter(i => i !== index);
      } else {
        this.expandedQuestions.push(index);
        await this.updateContentHeights();
      }
    },
    
    async toggleTutorial(tutorialId) {
      if (this.expandedTutorials.includes(tutorialId)) {
        this.expandedTutorials = this.expandedTutorials.filter(id => id !== tutorialId);
      } else {
        this.expandedTutorials.push(tutorialId);
        await this.updateContentHeights();
      }
    }
  },
  
  async mounted() {
    // Background image carousel
    this.interval = setInterval(() => {
      this.currentBgIndex = (this.currentBgIndex + 1) % this.backgroundImages.length;
    }, 10000);
    
    // Initialize content heights
    await this.updateContentHeights();
    
    // Update heights on window resize
    window.addEventListener('resize', this.updateContentHeights);
  },
  
  beforeUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    window.removeEventListener('resize', this.updateContentHeights);
  }
}
</script>

<style>
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  height: 0;
  opacity: 0;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.list-move {
  transition: transform 0.5s ease;
}
</style>